import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CurrencyFormat, Item } from '../../waitingnotes.component'; // ajuste le chemin si besoin
import { CssService } from 'src/app/services/css-service';

@Component({
    selector: 'app-select-items-dialog',
    templateUrl: './select-items-dialog.component.html',
    styleUrls: ['./select-items-dialog.component.css']
})
export class SelectItemsDialogComponent {
    items: Item[] = [];
    allItemsSelected = false;
    selectedUUIDS: { [uuid: string]: { checked: boolean; item: Item } } = {};

    constructor(
        public dialogRef: MatDialogRef<SelectItemsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { items: Item[], currencyFormat: CurrencyFormat },
        public cssService: CssService,
    ) {
        // Clone pour éviter de modifier la sélection principale directement avant validation
        this.items = JSON.parse(JSON.stringify(data.items));

        // Initialise partialQuantity
        const initPartialQuantity = (items: Item[]) => {
            for (const item of items) {
                item.partialQuantity = item.remainingQuantity > 0 ? item.remainingQuantity : 1;
            }
        };
        initPartialQuantity(this.items);
    }

    isClosing = false;
    selectedOption: Item[] | null = null;

    getCheckboxChecked(event: Event): boolean {
        return (event.target as HTMLInputElement).checked;
    }


    toggleItem(item: Item, checked: boolean): void {
        if (checked) {
            this.selectedUUIDS[item.uuid ?? `id-${item.id}`] = {
                checked: true,
                item: item
            };
        } else {
            delete this.selectedUUIDS[item.uuid ?? `id-${item.id}`];
        }

        item.selected = checked;
        if (item.selected) {
            item.partialQuantity = item.remainingQuantity > 0 ? item.remainingQuantity : 0;
        } else {
            item.partialQuantity = item.remainingQuantity > 0 ? item.remainingQuantity : 1;
        }
        // 🔁 Vérification auto de allItemsSelected

        // 1️⃣ S'il manque au moins un item sélectionnable non sélectionné → false
        const checkAllSelected = (items: Item[]): boolean => {
            return items.every(it =>
                (it.free === 1 || it.remainingQuantity === 0 || it.selected) &&
                (!it.children || checkAllSelected(it.children))
            );
        };

        // 2️⃣ S'il n’y a plus aucun item sélectionné → false aussi
        const anySelected = (items: Item[]): boolean => {
            return items.some(it =>
                it.selected || (it.children?.length && anySelected(it.children))
            );
        };

        this.allItemsSelected = checkAllSelected(this.items) && anySelected(this.items);
    }

    unselectAllItems(): void {
        const unselectRecursive = (items: Item[]) => {
            for (const item of items) {
                this.toggleItem(item, false);
                if (item.children?.length)
                    unselectRecursive(item.children);
            }
        };

        if (this.items?.length)
            unselectRecursive(this.items);

        this.allItemsSelected = false;
    }

    toggleSelectAllItems(): void {
        const items = this.items;
        if (!items) return;

        const shouldSelectAll = !this.anyItemSelected(items);
        this.allItemsSelected = shouldSelectAll;

        const toggleRecursive = (items: Item[]) => {
            for (const item of items) {
                if (item.free !== 1 && item.remainingQuantity !== 0)
                    this.toggleItem(item, shouldSelectAll);
                if (item.children?.length)
                    toggleRecursive(item.children);
            }
        };

        toggleRecursive(items);
    }

    anyItemSelected(items: Item[]): boolean {
        return items.some(item =>
            (item.selected && item.free !== 1 && !item.alreadyPaid) ||
            (item.children?.length && this.anyItemSelected(item.children))
        );
    }

    hasSelectedItems(): boolean {
        const check = (items: Item[]): boolean => {
            return items.some(item =>
                item.selected ||
                (item.children?.length && check(item.children))
            );
        };
        return check(this.items);
    }

    increasePartialQuantity(item: Item): void {
        if (item.partialQuantity < item.remainingQuantity)
            item.partialQuantity++;
    }

    decreasePartialQuantity(item: Item): void {
        if (item.partialQuantity > 1)
            item.partialQuantity--;
    }

    getTotalTtcWithChildrenAndQuantity(item: Item): number {
        let total = (item.remainingQuantity * item.unit_ttc || 0);

        // Ajuste en fonction de la quantité partielle
        if (item.partialQuantity && item.remainingQuantity > 0 && item.partialQuantity < item.remainingQuantity)
            total = item.partialQuantity * item.unit_ttc;

        if (item.children && item.children.length) {
            for (const child of item.children) {
                total += this.getTotalTtcWithChildrenAndQuantity(child);
            }
        }
        return total;
    }

    confirmSelection(): void {
        // Vérifie et ajuste les partialQuantity si besoin
        const adjustQuantityRecursive = (items: Item[]) => {
            for (const item of items) {
                if (item.selected) {
                    if (item.partialQuantity > item.remainingQuantity)
                        item.partialQuantity = item.remainingQuantity;
                    if (item.partialQuantity < 1)
                        item.partialQuantity = 1;
                }
                if (item.children?.length)
                    adjustQuantityRecursive(item.children);
            }
        };

        adjustQuantityRecursive(this.items);

        // Filtrer les items sélectionnés : tous les items sélectionnés avec une quantité partielle > 0
        const selectedItems: Item[] = [];
        const collectSelectedRecursive = (items: Item[]) => {
            for (const item of items) {
                if (item.selected && item.partialQuantity > 0)
                    selectedItems.push(item);
                if (item.children?.length)
                    collectSelectedRecursive(item.children);
            }
        };
        collectSelectedRecursive(this.items);

        this.selectedOption = selectedItems;
        this.isClosing = true;
    }

    cancel(): void {
        this.selectedOption = null;
        this.isClosing = true;
    }

    onAnimationEnd() {
        if (this.isClosing)
            this.dialogRef.close(this.selectedOption);
    }
}
